/**
 * HmOrders — kartoteka zamówień z Symfonii (ZMO — od odbiorców, ZWO — do dostawców).
 *
 * Zamówienia jako jedyne z pięciu kartotek nie mają wydruku — WebAPI Symfonii nie udostępnia
 * dla nich PDF-a. Formatka szczegółu nie pokazuje więc przycisku wydruku; to brak po stronie
 * Symfonii, nie nasza zaległość.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { HmOrderListItem } from '@saas/shared';
import { ClipboardList } from 'lucide-react';

import type { ColumnDefinition } from '@/components/configurable-table';
import { HmBulkApprovalButton } from '@/components/handel-magazyn/HmBulkApprovalButton';
import { day, HmListShell } from '@/components/handel-magazyn/HmListShell';
import { useApprovalColumn } from '@/components/handel-magazyn/useApprovalColumn';
import { useActiveCompanyScope } from '@/lib/use-active-company';

export function HmOrders() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const scope = useActiveCompanyScope();

  const approvalColumn = useApprovalColumn<HmOrderListItem>('order', scope.companyId);

  const columns = useMemo<ColumnDefinition<HmOrderListItem>[]>(
    () => [
      {
        id: 'documentNumber',
        header: t('tenant.handelMagazyn.columns.documentNumber'),
        accessor: (row) => row.documentNumber,
        defaultWidth: 170,
        renderCell: (_v: unknown, row: HmOrderListItem) => (
          <span className="flex items-center gap-2">
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${row.inBuffer ? 'bg-text-tertiary' : 'bg-emerald-500'}`}
            />
            <span className="font-mono text-xs text-text-primary">{row.documentNumber}</span>
          </span>
        ),
      },
      {
        id: 'typeCode',
        header: t('tenant.handelMagazyn.columns.typeCode'),
        accessor: (row) => row.typeCode,
        defaultWidth: 80,
      },
      {
        id: 'issueDate',
        header: t('tenant.handelMagazyn.columns.issueDate'),
        accessor: (row) => day(row.issueDate),
        defaultWidth: 130,
      },
      {
        id: 'buyerName',
        header: t('tenant.handelMagazyn.columns.contractor'),
        accessor: (row) => row.buyerName ?? '—',
        defaultWidth: 260,
      },
      {
        id: 'netValue',
        header: t('tenant.handelMagazyn.columns.net'),
        accessor: (row) => row.netValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 120,
      },
      {
        id: 'vatValue',
        header: t('tenant.handelMagazyn.columns.vat'),
        accessor: (row) => row.vatValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 110,
      },
      {
        id: 'grossValue',
        header: t('tenant.handelMagazyn.columns.gross'),
        accessor: (row) => row.grossValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 130,
        renderCell: (_v: unknown, row: HmOrderListItem) => (
          <span className="font-medium text-text-primary">{row.grossValue}</span>
        ),
      },
      {
        id: 'maturityDate',
        header: t('tenant.handelMagazyn.columns.maturityDate'),
        accessor: (row) => day(row.maturityDate),
        defaultWidth: 130,
      },
      {
        id: 'settled',
        header: t('tenant.handelMagazyn.detail.settled'),
        accessor: (row) =>
          row.settled ? t('tenant.handelMagazyn.detail.yes') : t('tenant.handelMagazyn.detail.no'),
        defaultWidth: 100,
      },
      // Stan obiegu akceptacji — jedyna kolumna czytana z NASZEJ bazy, nie z Symfonii.
      ...(approvalColumn ? [approvalColumn] : []),
    ],
    [t, approvalColumn],
  );

  return (
    <HmListShell<HmOrderListItem>
      title={t('tenant.handelMagazyn.orders.title')}
      resource="orders"
      queryKey="orders"
      tableId="hm_orders"
      testIdPrefix="hm-orders"
      columns={columns}
      getRowId={(row) => String(row.id)}
      selectable
      renderSelectionAction={({ selected }) => (
        <HmBulkApprovalButton<HmOrderListItem>
          kind="order"
          companyId={scope.companyId}
          selected={selected}
          getExternalId={(row) => row.id}
          getLabel={(row) => row.documentNumber}
        />
      )}
      emptyIcon={ClipboardList}
      groupBy={{ label: t('tenant.handelMagazyn.orders.kinds'), of: (row) => row.typeCode }}
      onRowClick={(row) => navigate(`/handel-magazyn/zamowienia/${row.id}`)}
    />
  );
}

export default HmOrders;
